import { IBeraWithdraworAbi } from '../abis/ibera'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, toNumber, type LstWithdrawalReader } from './shared'

/**
 * Infrared iBERA — withdrawal enumeration on the WithdraworLite global
 * queue.
 *
 * iBERA has no per-user request getter: `burn` mints a GLOBAL nonce and
 * `requests(requestId)` is keyed by that nonce. We therefore scan the
 * global queue and filter by the `receiver` field. Claimed/cleared
 * entries zero their `amount` (same convention as the Kinetiq reader),
 * so `amount > 0` is the "still open" filter. Claimability is
 * frontier-based: `requestId < requestsFinalisedUntil()`.
 *
 * Stage 1: `requestLength()` + `requestsFinalisedUntil()`.
 * Stage 2: `requests(i)` over the most recent window.
 *
 * The queue is global and unbounded; to keep the multicall payload sane
 * we scan only the most recent `MAX_SCAN` entries. Open requests cluster
 * near the tail (claimed ones at the front are zeroed), so this captures
 * a user's live withdrawals in practice. Older un-claimed requests beyond
 * the window are not enumerated — acceptable under the "we may not know
 * all your requests" model.
 */
const MAX_SCAN = 3000

export const readerIbera: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const withdrawor = entry.withdrawalContract
    if (!withdrawor) return []

    let stage1: unknown[]
    try {
      stage1 = (await multicallRetry({
        chain: chainId,
        calls: [
          { address: withdrawor, name: 'requestLength', params: [] },
          { address: withdrawor, name: 'requestsFinalisedUntil', params: [] },
        ] as unknown as never[],
        abi: [IBeraWithdraworAbi, IBeraWithdraworAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }
    const length = toNumber(stage1[0]) ?? 0
    const frontier = toBigInt(stage1[1]) ?? 0n
    if (length === 0) return []

    const start = length > MAX_SCAN ? length - MAX_SCAN : 0
    const ids = Array.from({ length: length - start }, (_, k) => start + k)

    let stage2: unknown[]
    try {
      stage2 = (await multicallRetry({
        chain: chainId,
        calls: ids.map((i) => ({
          address: withdrawor,
          name: 'requests',
          params: [BigInt(i)],
        })) as unknown as never[],
        abi: ids.map(() => IBeraWithdraworAbi) as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    const target = user.toLowerCase()
    const out: LstWithdrawalRequest[] = []
    for (let k = 0; k < ids.length; k++) {
      const r = stage2[k] as
        | { receiver?: unknown; amount?: unknown }
        | unknown[]
        | undefined
      if (!r) continue
      const receiver = Array.isArray(r) ? r[2] : r.receiver
      const amount = Array.isArray(r) ? toBigInt(r[3]) : toBigInt(r.amount)
      if (
        typeof receiver !== 'string' ||
        receiver.toLowerCase() !== target ||
        amount === undefined ||
        amount === 0n
      ) {
        continue
      }
      const id = BigInt(ids[k])
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(ids[k]),
        amountUnderlying: amount.toString(),
        status: id < frontier ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
